import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { Controller, useForm } from 'react-hook-form';
import {
	asyncCallDone,
	asyncCallError,
	asyncCallStart,
} from '../../data/stores/projectStore';
import { showAlert } from '../../data/stores/alertStore';
import ScreenBusySpinner from '../common/ScreenBusySpinner';
import Input from '../common/form/Input';
import { AsyncStage } from '../../data/reduxCommon';
import ModalHeader from '../common/ModalHeader';
import { updateCompanyProjectFolder } from '../../services/company-service';
import { refreshCompanySuccess } from '../../data/stores/companyStore';
import Label from '../common/Label';
import { ProjectFolder } from '../../data/models/company';

interface EditProjectFolderModalProps {
	open: boolean;
	onClose: () => void;
	folderId: string;
	folderName: string;
	folderPath?: string[];
	parentFolderId?: string | null; // Kept for backward compatibility
}

interface FormData {
	name: string;
}

export default function EditProjectFolderModal({
	open,
	onClose,
	folderId,
	folderName,
	folderPath = [],
	parentFolderId = null,
}: EditProjectFolderModalProps) {
	const dispatch = useDispatch<AppDispatch>();
	const { stage } = useSelector((state: RootState) => state.projectState);
	const { company } = useSelector((state: RootState) => state.companyState);

	// Determine if this is a subfolder (either via folderPath or parentFolderId)
	const isSubFolder = folderPath.length > 0 || !!parentFolderId;
	const [parentFolderName, setParentFolderName] = useState<string>('');

	// Find parent folder name when component mounts or path changes
	useEffect(() => {
		if (company?.projectFolders) {
			if (folderPath.length > 0) {
				// For new approach using folderPath, find the last folder in the path
				const lastParentId = folderPath[folderPath.length - 1];
				let currentFolders: ProjectFolder[] = company.projectFolders;
				let currentFolder: ProjectFolder | undefined = undefined;

				// Navigate through the folder path to find the target folder
				for (const folderId of folderPath) {
					currentFolder = currentFolders.find(
						(f) => f.id === folderId
					);
					if (!currentFolder) break;
					if (currentFolder.id === lastParentId) break;
					currentFolders = currentFolder.subFolders || [];
				}

				if (currentFolder) {
					setParentFolderName(currentFolder.display);
				}
			} else if (parentFolderId) {
				// Legacy approach - direct parentFolderId
				const parentFolder = company.projectFolders.find(
					(folder) => folder.id === parentFolderId
				);
				if (parentFolder) {
					setParentFolderName(parentFolder.display);
				}
			}
		}
	}, [folderPath, parentFolderId, company]);

	const { handleSubmit, control, reset, formState } = useForm<FormData>({
		defaultValues: {
			name: folderName,
		},
		mode: 'onSubmit',
	});
	const { errors } = formState;

	const resetForm = () => {
		reset({ name: folderName });
	};

	const handleClose = () => {
		onClose();
		resetForm();
	};

	const handleUpdateProjectFolder = async (data: FormData) => {
		// Skip update if name hasn't changed
		if (data.name === folderName) {
			handleClose();
			return;
		}

		dispatch(asyncCallStart());
		try {
			const updateData = {
				id: folderId,
				display: data.name.toUpperCase(),
				// Use folderPath if available, otherwise use parentFolderId for backwards compatibility
				...(folderPath.length > 0 && { folderPath }),
				...(parentFolderId && !folderPath.length && { parentFolderId }),
			};

			const response = await updateCompanyProjectFolder(updateData);
			dispatch(refreshCompanySuccess(response));
			dispatch(
				showAlert({
					message: `${
						isSubFolder ? 'Sub folder' : 'Project folder'
					} updated successfully`,
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('error', error);
			dispatch(asyncCallError(error));
			throw error;
		} finally {
			handleClose();
			dispatch(asyncCallDone());
		}
	};

	return (
		<Modal open={open} handleClose={handleClose}>
			{open ? (
				<>
					<ScreenBusySpinner />
					<FormContainer>
						<ModalHeader>
							{isSubFolder
								? 'Edit Sub Folder'
								: 'Edit Project Folder'}
						</ModalHeader>
						{isSubFolder && parentFolderName && (
							<ParentFolderContainer>
								<Label>Parent Folder</Label>
								<ParentFolderValue>
									{parentFolderName}
								</ParentFolderValue>
							</ParentFolderContainer>
						)}
						<InputContainer>
							<Controller
								name="name"
								control={control}
								rules={{ required: 'Name is required' }}
								render={({ field }) => (
									<Input
										{...field}
										label={
											isSubFolder
												? 'Sub Folder Name'
												: 'Folder Name'
										}
										id="folderName"
										onChange={(e) => {
											const newValue = e.target.value;
											const regex = /[^A-Za-z0-9 -_.]/g;
											if (regex.test(newValue)) return;
											field.onChange(
												newValue.toUpperCase()
											);
										}}
										error={errors.name}
										helperText={errors.name?.message}
										placeholder="Enter folder name"
									/>
								)}
							/>
							<SaveCancelButtons
								handleSave={handleSubmit(
									handleUpdateProjectFolder
								)}
								handleCancel={handleClose}
								saveDisabled={stage === AsyncStage.Busy}
								cancelDisabled={stage === AsyncStage.Busy}
								saveText="Update"
							/>
						</InputContainer>
					</FormContainer>
				</>
			) : null}
		</Modal>
	);
}

const FormContainer = styled.div`
	text-align: left;
`;

const ParentFolderContainer = styled.div`
	margin-bottom: 25px;
`;

const ParentFolderValue = styled.div`
	font-weight: 500;
	margin-top: 5px;
`;

const InputContainer = styled.div`
	max-width: 450px;
	margin-bottom: 20px;
	display: flex;
	justify-content: center;
	margin-right: auto;
	margin-left: auto;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(4)};
`;
